import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SharedModule } from '@aiwyn/shared';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../../../../routing-constants';

/**
 * CRL list-view page header (REQ-2): breadcrumb `Tasks > [Name]`, the list name
 * as the page H1, the associated client entity name, and a description that
 * truncates to ~3 lines with an inline View More / View Less toggle. Mirrors
 * `DeliverableDetailHeaderComponent`'s truncation pattern.
 *
 * The header states no roll-up status. Where the request stands is already read
 * off the progress tile, the action-required banner and the item rows, each of
 * which is more actionable than a single label over the whole request.
 */
@Component({
  selector: 'cxp-crl-page-header',
  templateUrl: './crl-page-header.component.html',
  styleUrls: ['./crl-page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, RouterLink],
})
export class CrlPageHeaderComponent {
  readonly name = input.required<string>();
  /**
   * Title eyebrow shown above the name — engagement/category context joined with
   * the client (e.g. `Audit · Acme LLC`), pre-composed by the selector. Falls
   * back to the client name alone when no engagement context resolves.
   */
  readonly eyebrow = input<string | null>(null);
  readonly description = input<string>('');
  /** Hidden for a secure-link guest, who has no access to the Tasks page. */
  readonly showBreadcrumb = input<boolean>(true);

  readonly tasksLink = [`/${ROUTE_TOKEN_MAIN_BASE}`, ROUTE_TOKEN_CXP_TASKS];

  /** Toggled by View More / View Less; collapsed clamps to 3 lines via CSS. */
  protected readonly expanded = signal(false);

  protected readonly toggleLabel = computed(() => (this.expanded() ? 'View Less' : 'View More'));

  toggleExpanded(): void {
    this.expanded.update((value) => !value);
  }
}
